import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid,
  IconButton, Stack, TextField, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, Button, Paper,
  Alert, Chip, InputAdornment
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { exportToExcel, exportToPrintWindow } from '../utils/exportUtils';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchClasses, deleteClass, clearDeleteError } from '../slices/classesSlice';
import SchoolIcon from '@mui/icons-material/School';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useToast } from '../context/ToastContext';
import { motion } from 'framer-motion';

const MotionPaper = motion(Paper);
const MotionCard = motion(Card);

const ClassesPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const { showToast } = useToast();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { list: classList, status, deleteError } = useSelector((state) => state.classes);

  useEffect(() => {
    dispatch(fetchClasses());
  }, [dispatch]);

  // Handle delete errors
  useEffect(() => {
    if (deleteError) {
      setErrorMessage(deleteError.message || 'حدث خطأ أثناء حذف الصف');
      setErrorDialogOpen(true);
    }
  }, [deleteError]);

  const handleClassClick = (id) => {
    navigate(`/classes/view/${id}`);
  };

  const handleAddClick = () => {
    navigate('/classes/create');
  };

  const handleDeleteClick = (id, e) => {
    e.stopPropagation();
    setSelectedClassId(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      // Clear any previous delete errors
      dispatch(clearDeleteError());
      
      const result = await dispatch(deleteClass(selectedClassId));
      
      if (deleteClass.fulfilled.match(result)) {
        showToast('تم حذف الصف بنجاح', 'success');
        setDeleteDialogOpen(false);
        setSelectedClassId(null);
      }
    } catch (err) {
      console.error('Failed to delete class:', err);
      // Error will be handled by the useEffect that watches deleteError
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSelectedClassId(null);
  };

  const handleCloseErrorDialog = () => {
    setErrorDialogOpen(false);
    setDeleteDialogOpen(false);
    dispatch(clearDeleteError());
  };

  const handleEditClick = (id, e) => {
    e.stopPropagation();
    navigate(`/classes/edit/${id}`);
  };

  const handlePrint = () => {
    exportToPrintWindow(exportData, exportColumns, 'الصفوف');
  };

  // Enhanced filtering that also handles teacher names properly
  const filteredClasses = classList.filter((c) => {
    const className = c.name?.toLowerCase() || '';
    const teacherName = c.teacher?.toLowerCase() || '';
    const query = searchQuery.toLowerCase();
    
    return className.includes(query) || teacherName.includes(query);
  });

  // Enhanced export data that excludes archived students
  const exportColumns = [
    { header: 'اسم الصف', key: 'name' },
    { header: 'عدد الطلاب', key: 'active_student_count' },
    { header: 'المعلم المسؤول', key: 'teacher' },
  ];
  
  const exportData = filteredClasses.map(c => {
    // Calculate active students count (excluding archived)
    const activeStudentCount = c.students 
      ? c.students.filter(student => !student.is_archived).length 
      : c.student_count || 0;
    
    return {
      name: c.name,
      active_student_count: activeStudentCount,
      teacher: c.teacher || 'غير محدد',
    };
  });

  // Helper function to get active student count
  const getActiveStudentCount = (classItem) => {
    if (classItem.students) {
      return classItem.students.filter(student => !student.is_archived).length;
    }
    // Fallback to student_count if students array is not available
    return classItem.student_count || 0;
  };

  // Helper function to get teacher name
  const getTeacherName = (classItem) => {
    return classItem.teacher || 'غير محدد';
  };

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SchoolIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                إدارة الصفوف
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                عرض وإدارة الصفوف الدراسية والمعلمين المسؤولين
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            onClick={handleAddClick}
            startIcon={<AddIcon />}
            data-guide-id="guide-add-class"
            sx={{
              bgcolor: '#fff',
              color: '#4338CA',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              boxShadow: 'none',
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              '&:hover': { bgcolor: '#EEF2FF', boxShadow: 'none' },
            }}
          >
            إضافة صف جديد
          </Button>
        </MotionPaper>

        {/* Export & Summary Bar */}
        <MotionPaper
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            mb: 3,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => exportToExcel(exportData, exportColumns, 'الصفوف')}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                px: 2.5,
                fontFamily: 'Cairo, Tajawal, sans-serif',
                borderColor: '#16A34A',
                color: '#16A34A',
                '&:hover': { borderColor: '#16A34A', bgcolor: '#DCFCE7' },
              }}
            >
              تصدير Excel
            </Button>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={handlePrint}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                px: 2.5,
                fontFamily: 'Cairo, Tajawal, sans-serif',
                borderColor: '#4338CA',
                color: '#4338CA',
                '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
              }}
            >
              طباعة
            </Button>
          </Stack>

          {/* Summary info */}
          <Chip
            label={`عرض ${filteredClasses.length} من ${classList.length} صف`}
            sx={{
              bgcolor: '#EEF2FF',
              color: '#4338CA',
              fontWeight: 700,
              fontFamily: 'Cairo, Tajawal, sans-serif',
              borderRadius: 2,
            }}
          />
        </MotionPaper>

        {/* Search Bar */}
        <MotionPaper
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15, ease: 'easeOut' }}
          sx={{
            mb: 3,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
          }}
        >
          <TextField
            label="بحث عن صف أو معلم"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            dir="rtl"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#6B7280' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              maxWidth: 420,
              fontFamily: 'Cairo, Tajawal, sans-serif',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2.5,
                fontFamily: 'Cairo, Tajawal, sans-serif',
              },
              '& label': { fontFamily: 'Cairo, Tajawal, sans-serif' },
            }}
          />
        </MotionPaper>

        <Grid
          container
          spacing={3}
        >
          {filteredClasses.map((classItem, idx) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              key={classItem.id}
            >
              <MotionCard
                onClick={() => handleClassClick(classItem.id)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + idx * 0.05, duration: 0.35, ease: 'easeOut' }}
                whileHover={{ y: -4 }}
                elevation={0}
                sx={{
                  borderRadius: 3,
                  p: { xs: 2, sm: 2.5 },
                  bgcolor: '#fff',
                  boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                  border: '1px solid #E5E7EB',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'box-shadow 0.25s ease, border-color 0.25s ease',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(67,56,202,0.14)',
                    borderColor: '#C7D2FE',
                  },
                }}
              >
                {/* Action Icons */}
                <Stack useFlexGap
                  direction="row"
                  spacing={0.5}
                  sx={{ position: 'absolute', top: 10, left: 10 }}
                >
                  <Tooltip title="تعديل">
                    <IconButton
                      size="small"
                      onClick={(e) => handleEditClick(classItem.id, e)}
                      sx={{
                        color: '#D97706',
                        bgcolor: '#FEF3C7',
                        '&:hover': { bgcolor: '#FDE68A' },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="حذف">
                    <IconButton
                      size="small"
                      onClick={(e) => handleDeleteClick(classItem.id, e)}
                      sx={{
                        color: '#DC2626',
                        bgcolor: '#FEE2E2',
                        '&:hover': { bgcolor: '#FECACA' },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>

                <CardContent sx={{ p: 0, pt: 1, '&:last-child': { pb: 0 } }}>
                  <Stack useFlexGap direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        bgcolor: '#EEF2FF',
                        borderRadius: 2,
                        width: 44,
                        height: 44,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <SchoolIcon sx={{ color: '#4338CA', fontSize: 24 }} />
                    </Box>
                    <Typography
                      variant="h6"
                      fontWeight={800}
                      sx={{
                        color: '#1E1B4B',
                        fontFamily: 'Cairo, Tajawal, sans-serif',
                        lineHeight: 1.3,
                      }}
                    >
                      {classItem.name}
                    </Typography>
                  </Stack>

                  <Stack spacing={1.25}>
                    <Stack useFlexGap direction="row" alignItems="center" spacing={1}>
                      <PeopleAltOutlinedIcon sx={{ color: '#6B7280', fontSize: 20 }} />
                      <Typography
                        variant="body2"
                        sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                      >
                        عدد الطلاب
                      </Typography>
                      <Chip
                        label={getActiveStudentCount(classItem)}
                        size="small"
                        sx={{
                          bgcolor: '#E0F2FE',
                          color: '#0284C7',
                          fontWeight: 700,
                          fontFamily: 'Cairo, Tajawal, sans-serif',
                          ml: 'auto',
                        }}
                      />
                    </Stack>
                    <Stack useFlexGap direction="row" alignItems="center" spacing={1}>
                      <PersonOutlineIcon sx={{ color: '#6B7280', fontSize: 20 }} />
                      <Typography
                        variant="body2"
                        sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                      >
                        المعلم المسؤول
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#1E1B4B',
                          fontWeight: 600,
                          fontFamily: 'Cairo, Tajawal, sans-serif',
                          ml: 'auto',
                        }}
                      >
                        {getTeacherName(classItem)}
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>
              </MotionCard>
            </Grid>
          ))}
        </Grid>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCancelDelete}
          dir="rtl"
          PaperProps={{
            sx: {
              borderRadius: 3,
              fontFamily: 'Cairo, Tajawal, sans-serif',
              boxShadow: '0 8px 32px rgba(67,56,202,0.18)',
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 800,
              color: '#1E1B4B',
              fontFamily: 'Cairo, Tajawal, sans-serif',
            }}
          >
            تأكيد الحذف
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
              هل أنت متأكد أنك تريد حذف هذا الصف؟
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button
              onClick={handleCancelDelete}
              variant="outlined"
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                borderColor: '#E5E7EB',
                color: '#6B7280',
                '&:hover': { borderColor: '#D1D5DB', bgcolor: '#F8F9FF' },
              }}
            >
              إلغاء
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="outlined"
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                borderColor: '#DC2626',
                color: '#DC2626',
                '&:hover': { borderColor: '#DC2626', bgcolor: '#FEE2E2' },
              }}
            >
              حذف
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Error Dialog */}
        <Dialog
          open={errorDialogOpen}
          onClose={handleCloseErrorDialog}
          dir="rtl"
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              fontFamily: 'Cairo, Tajawal, sans-serif',
              boxShadow: '0 8px 32px rgba(67,56,202,0.18)',
            },
          }}
        >
          <DialogTitle
            sx={{
              color: '#DC2626',
              fontWeight: 800,
              fontFamily: 'Cairo, Tajawal, sans-serif',
            }}
          >
            تعذر حذف الصف
          </DialogTitle>
          <DialogContent>
            <Alert
              severity="error"
              sx={{
                mt: 1,
                mb: 2,
                borderRadius: 2,
                fontFamily: 'Cairo, Tajawal, sans-serif',
              }}
            >
              {errorMessage}
            </Alert>
            <Typography sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
              يرجى حل المشكلة المذكورة أعلاه قبل محاولة حذف الصف مرة أخرى.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleCloseErrorDialog}
              variant="contained"
              sx={{
                minWidth: 100,
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                bgcolor: '#4338CA',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
              }}
            >
              موافق
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default ClassesPage;